/**
 * Pure-function verification for authenticated quote/reply previews.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/QuotedReplyVerifier.swift` exactly so
 * both platforms produce identical verification outcomes for the same inputs.
 *
 * At render time the receiver hands the wire-decoded `QuotedReplyPayload`
 * plus the locally-stored copy of the original message (if any). The
 * verifier compares the local plaintext's SHA256 against the wire hash
 * and the local author against the claimed author. The output drives a
 * four-way render decision:
 *
 *   { kind: "verified", localPlaintext }
 *       → render the local plaintext, trust the quote
 *
 *   { kind: "unverified" }
 *       → render the wire preview, dim/flag it (we cannot check; the
 *         original isn't in our local store)
 *
 *   { kind: "hashMismatch" }
 *       → the wire hash does not match the local plaintext — either the
 *         original was edited or a hostile relay swapped the body. Show
 *         a warning.
 *
 *   { kind: "authorMismatch", claimed, actual }
 *       → the wire claims a different author than what we locally have
 *         for this messageId. Surface the claim vs. the actual author
 *         so the UI can flag the attempted spoofing.
 *
 * Verification order mirrors iOS: author check before hash check (so an
 * attacker who lies about both surfaces as `authorMismatch` rather than
 * `hashMismatch`, which is the more informative failure for the UI).
 *
 * No I/O, no statics, no globals — trivially testable.
 *
 * @module QuotedReplyVerifier
 */

import type { QuotedReplyPayload } from "./QuotedReply";
import { QuotedReplyCodec } from "./QuotedReply";

// ---------------------------------------------------------------------------
// Tagged union result type
// ---------------------------------------------------------------------------

/** Verification succeeded — `localPlaintext` is the trusted body to render. */
export interface VerifiedResult {
  readonly kind: "verified";
  readonly localPlaintext: string;
}

/**
 * The original message is not in our local store (joined late, expired,
 * deleted, etc.). The wire `quotedPreview` may be shown, but must be flagged
 * as unverified.
 */
export interface UnverifiedResult {
  readonly kind: "unverified";
}

/**
 * The wire `quotedAuthorId` does not match the author stored locally for this
 * messageId. `actual` is the locally-authoritative author ID (never the
 * peer-supplied value). If the local record happens to have no author field,
 * `actual` is `undefined`.
 */
export interface AuthorMismatchResult {
  readonly kind: "authorMismatch";
  readonly claimed: string;
  readonly actual: string | undefined;
}

/**
 * The SHA256 of the local plaintext does not match `quotedPlaintextHash`. The
 * original was either edited after the reply was sent, or a hostile relay
 * substituted the body.
 */
export interface HashMismatchResult {
  readonly kind: "hashMismatch";
}

/**
 * Four-way tagged union returned by `QuotedReplyVerifier.verify(...)`.
 * Mirrors `QuoteVerification` in iOS exactly.
 */
export type QuoteVerification =
  | VerifiedResult
  | UnverifiedResult
  | AuthorMismatchResult
  | HashMismatchResult;

// ---------------------------------------------------------------------------
// Verifier
// ---------------------------------------------------------------------------

/**
 * Locally-stored copy of the original message, supplied by the caller.
 * The verifier deliberately does not perform I/O — the caller looks up local
 * storage and passes the result here.
 *
 * `null` means the original is not cached locally (late join, expiry, etc.).
 */
export interface LocalOriginal {
  readonly authorId: string;
  readonly plaintext: string;
}

/**
 * Verify a quoted-reply payload against locally-stored data for the message
 * being quoted.
 *
 * `localOriginal === null` means we don't have the original on disk — the
 * quote is rendered as `{ kind: "unverified" }`.
 *
 * The check order mirrors iOS `QuotedReplyVerifier.verify(payload:localOriginal:)`:
 * author first, then hash. This means a payload where BOTH author and hash
 * mismatch surfaces as `authorMismatch`.
 */
export async function verify(
  payload: QuotedReplyPayload,
  localOriginal: LocalOriginal | null,
): Promise<QuoteVerification> {
  if (localOriginal === null) {
    return { kind: "unverified" };
  }

  // Author check (advisory field, but surfaces spoofing attempts).
  if (localOriginal.authorId !== payload.quotedAuthorId) {
    return {
      kind: "authorMismatch",
      claimed: payload.quotedAuthorId,
      actual: localOriginal.authorId,
    };
  }

  // Hash check — the integrity anchor.
  const recomputed = await QuotedReplyCodec.computeHash(localOriginal.plaintext);
  if (recomputed !== payload.quotedPlaintextHash) {
    return { kind: "hashMismatch" };
  }

  return { kind: "verified", localPlaintext: localOriginal.plaintext };
}

export const QuotedReplyVerifier = { verify } as const;
